import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useCheckPolicy } from '../../../../../../hooks'
import { THINGS } from '../../../../../../policies/admin'
import Loading from '../../../../../molecules/Loading'
import ErrorDisplay from '../../../../../molecules/ErrorDisplay'
import AdminPage from '../../../shared/AdminPage'
import OperationalTable from '../../../../../organisms/OperationalTable'
import { useDeleteThingConfiguration, useGetThingSchemas, useGetThingCodesInUse } from '../../../../../../api/things'
import ConfirmationModal from '../../../../../molecules/ConfirmationModal'
import SydButton from '../../../../../commonDesign/Button'
import ThingSchemaModal from './thingSchemaModal'
import ThingAddModal from './thingAddModal'

const ContentView = () => {
  const levelTypeMap = {
    1: 'Account',
    3: 'Product',
    201: 'Client'
  }
  const { data: rawData, isLoading } = useGetThingSchemas([1, 3, 201])
  const data = isLoading ? [] : rawData.schema
  const [thingSchema, setThingSchema] = React.useState(null)

  const { data: codesInUse, isLoading: isLoadingCodes } = useGetThingCodesInUse()
  const codesInUseSet = useMemo(() => {
    if (!codesInUse || !Array.isArray(codesInUse)) return new Set()
    return new Set(codesInUse.map(item => item.code))
  }, [codesInUse])

  const [deletedId, setDeletedId] = React.useState(null)
  const { mutateAsync, isLoading: deleteLoading } = useDeleteThingConfiguration()
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)

  const [thingModalOpen, setThingModalOpen] = React.useState(false)
  const [thingAddOpen, setThingAddOpen] = React.useState(false)
  const onAddClick = (addModal) => {
    setThingSchema({
      name: '',
      code: '',
      description: '',
      category: ''
    })
    addModal ? setThingAddOpen(true) : setThingModalOpen(true)
  }
  const defaultColumnConfig = {
    columns: [
      { Header: 'Id', accessor: 'thingConfigurationId', type: 'number' },
      {
        Header: 'Level Type',
        type: 'string',
        Cell: ({ row }) => levelTypeMap[row.original.levelTypeId]
      },
      { Header: 'Category', accessor: 'category', type: 'string' },
      { Header: 'Code', accessor: 'code', type: 'string' },
      { Header: 'Name', accessor: 'name', type: 'string' },
      { Header: 'Description', accessor: 'description', type: 'string' },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({ row }) => ActionsCell({
          canEditContent: canEditContent,
          canDelete: !codesInUseSet.has(row.original.code),
          deleteFunction: () => {
            setDeletedId(row.original.thingConfigurationId)
            setDeleteModalOpen(true)
          },
          editFunction: () => {
            setThingModalOpen(true)
            setThingSchema(row.original)
          }
        })
      }
    ],
    defaultSort: [{ id: 'thingConfigurationId', desc: false }]
  }
  const canEditContent = useCheckPolicy(THINGS.editThings)

  if (isLoading || isLoadingCodes) {
    return <Loading />
  }
  if (data.length === 0) {
    return <ErrorDisplay soft error='No Schemas found' />
  }
  return (
    <AdminPage>
      <ConfirmationModal
        open={deleteModalOpen || deleteLoading}
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={async () => {
          const payload = {
            thingConfigurationId: deletedId
          }
          const result = await mutateAsync(payload)
          if (result.success) {
            setDeletedId(null)
            setDeleteModalOpen(false)
          } else {
            console.error(result)
          }
        }}
        isLoading={deleteLoading}
        confirmationType='delete'
        confirmationText='Are you sure you want to delete?'
        buttonLabelCancel='Cancel'
        buttonLabelConfirm='Delete'
      />

      <>
        {thingSchema && (
          <ThingSchemaModal
            open={thingModalOpen}
            onClose={() => setThingModalOpen(false)}
            thingSchema={thingSchema}
          />
        )}
        {thingAddOpen && (
          <ThingAddModal
            open={thingAddOpen}
            onClose={() => setThingAddOpen(false)}
          />
        )}
        <OperationalTable.Wrapper>
          {canEditContent && (
            <div className='flex flex-row justify-end mb-4'>
              <SydButton size='md' variant='primary' icon='add' onClick={() => onAddClick(true)}>Add Configuration</SydButton>
            </div>
          )}
          <OperationalTable
            variant='v2'
            mode='auto'
            itemName='Content Types'
            columns={defaultColumnConfig.columns}
            defaultSort={defaultColumnConfig.defaultSort}
            data={data}
            total={data.length}
          />
        </OperationalTable.Wrapper>
      </>
    </AdminPage>
  )
}

export default ContentView

export const ActionsCell = ({ canDelete, deleteFunction, editFunction, canEditContent }) => {
  return (
    <div className='flex flex-row items-center'>
      <div className='flex flex-row gap-2 pr-4'>
        <SydButton size='sm' variant='outline' icon='edit' onClick={() => editFunction()} disabled={!canEditContent}>Edit</SydButton>
        <SydButton size='sm' variant='outline' priority='warning' icon='delete' onClick={deleteFunction} disabled={!canDelete || !canEditContent}>Delete</SydButton>
      </div>
    </div>
  )
}

ActionsCell.propTypes = {
  canDelete: PropTypes.bool,
  editFunction: PropTypes.func,
  deleteFunction: PropTypes.func,
  canEditContent: PropTypes.bool
}
